import React from 'react';

const TechnologyFreedom = () => {
  return (
    <section id="creativity-problem" className="min-h-screen flex items-center py-20 px-4">
      <div className="container mx-auto">
        <div className="max-w-6xl mx-auto">
          {/* Section Title */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 font-heading">
              We Have <span className="bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">Talent</span>,<br />
              But Where's the <span className="bg-gradient-to-r from-warning to-danger bg-clip-text text-transparent">Creativity</span>?
            </h2>
          </div>

          {/* The Paradox */}
          <div className="mb-16 bg-gradient-to-br from-dark/60 to-darker/60 backdrop-blur-sm border-2 border-primary/30 rounded-3xl p-10 md:p-12">
            <div className="space-y-6 text-lg md:text-xl text-gray-300 leading-relaxed">
              <p>
                We have <span className="text-accent font-semibold">brilliant engineers, doctors, and designers</span> — people who work for world-class companies like Google, Microsoft, Amazon.
              </p>
              <p className="text-2xl text-white font-semibold">
                But how many <span className="text-primary">own products</span> or <span className="text-secondary">global companies</span> come from our region?
              </p>
              <p className="text-3xl text-danger font-bold text-center py-6">
                Very few.
              </p>
              <p className="text-xl text-warning italic text-center border-l-4 border-warning pl-6">
                Because creativity doesn't grow in fear.<br />
                You can't innovate when your entire life has been about<br />
                not disappointing your parents or your society.
              </p>
            </div>
          </div>

          {/* Real Examples - What Others Are Building */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-white mb-8 text-center font-heading">
              While We Follow Rules, Others Build The Future
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* OpenAI Example */}
              <div className="bg-gradient-to-br from-primary/10 to-transparent border-2 border-primary/30 rounded-3xl p-8 hover:border-primary/50 transition-all duration-300">
                <div className="flex items-start gap-4 mb-4">
                  <div className="text-4xl">🤖</div>
                  <div>
                    <h4 className="text-2xl font-bold text-primary mb-2">ChatGPT (OpenAI)</h4>
                    <p className="text-sm text-gray-500">USA, 2022</p>
                  </div>
                </div>
                <p className="text-gray-300 leading-relaxed mb-3">
                  Team that dared to think differently about AI, questioned existing approaches.
                </p>
                <p className="text-white font-semibold">
                  100M+ users in 2 months — fastest-growing app in history.
                </p>
              </div>

              {/* Spotify Example */}
              <div className="bg-gradient-to-br from-accent/10 to-transparent border-2 border-accent/30 rounded-3xl p-8 hover:border-accent/50 transition-all duration-300">
                <div className="flex items-start gap-4 mb-4">
                  <div className="text-4xl">🎵</div>
                  <div>
                    <h4 className="text-2xl font-bold text-accent mb-2">Spotify</h4>
                    <p className="text-sm text-gray-500">Sweden, 2006</p>
                  </div>
                </div>
                <p className="text-gray-300 leading-relaxed mb-3">
                  Two friends took a risk to solve music piracy their own way.
                </p>
                <p className="text-white font-semibold">
                  Now worth $50+ billion, changed how world listens to music.
                </p>
              </div>

              {/* Canva Example */}
              <div className="bg-gradient-to-br from-secondary/10 to-transparent border-2 border-secondary/30 rounded-3xl p-8 hover:border-secondary/50 transition-all duration-300">
                <div className="flex items-start gap-4 mb-4">
                  <div className="text-4xl">🎨</div>
                  <div>
                    <h4 className="text-2xl font-bold text-secondary mb-2">Canva</h4>
                    <p className="text-sm text-gray-500">Australia, 2013</p>
                  </div>
                </div>
                <p className="text-gray-300 leading-relaxed mb-3">
                  A woman who challenged traditional design tools when everyone said it's too complex.
                </p>
                <p className="text-white font-semibold">
                  135M+ users, democratized graphic design for everyone.
                </p>
              </div>

              {/* Figma Example */}
              <div className="bg-gradient-to-br from-warning/10 to-transparent border-2 border-warning/30 rounded-3xl p-8 hover:border-warning/50 transition-all duration-300">
                <div className="flex items-start gap-4 mb-4">
                  <div className="text-4xl">✏️</div>
                  <div>
                    <h4 className="text-2xl font-bold text-warning mb-2">Figma</h4>
                    <p className="text-sm text-gray-500">USA, 2016</p>
                  </div>
                </div>
                <p className="text-gray-300 leading-relaxed mb-3">
                  Reimagined collaborative design when everyone used desktop apps.
                </p>
                <p className="text-white font-semibold">
                  Acquired by Adobe for $20 billion in 2022.
                </p>
              </div>
            </div>

            <div className="mt-8 text-center bg-gradient-to-r from-primary/10 to-accent/10 border-2 border-primary/30 rounded-2xl px-8 py-6 max-w-3xl mx-auto">
              <p className="text-lg text-gray-300">
                <span className="text-white font-semibold">Common pattern?</span><br />
                They all <span className="text-accent font-bold">questioned the status quo</span>, took <span className="text-primary font-bold">risks</span>, and weren't afraid of <span className="text-secondary font-bold">failure</span>.
              </p>
            </div>
          </div>

          {/* What South Asia Has */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-white mb-8 text-center font-heading">
              What Do We Have From South Asia?
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Positive Examples */}
              <div className="bg-gradient-to-br from-accent/10 to-transparent border-2 border-accent/30 rounded-2xl p-6">
                <div className="text-3xl mb-3">✅</div>
                <h5 className="text-lg font-bold text-accent mb-2">Paytm (India)</h5>
                <p className="text-gray-400 text-sm">Digital payments pioneer</p>
              </div>
              
              <div className="bg-gradient-to-br from-accent/10 to-transparent border-2 border-accent/30 rounded-2xl p-6">
                <div className="text-3xl mb-3">✅</div>
                <h5 className="text-lg font-bold text-accent mb-2">Careem</h5>
                <p className="text-gray-400 text-sm">Ride-hailing (sold to Uber for $3.1B)</p>
              </div>
              
              <div className="bg-gradient-to-br from-accent/10 to-transparent border-2 border-accent/30 rounded-2xl p-6">
                <div className="text-3xl mb-3">✅</div>
                <h5 className="text-lg font-bold text-accent mb-2">Byju's (India)</h5>
                <p className="text-gray-400 text-sm">Ed-tech unicorn</p>
              </div>
            </div>

            <div className="mt-8 text-center bg-gradient-to-r from-warning/20 to-danger/20 border-2 border-warning/40 rounded-2xl px-8 py-6 max-w-3xl mx-auto">
              <p className="text-xl text-white leading-relaxed">
                These exist, but they're <span className="text-warning font-bold">exceptions, not the norm</span>.<br />
                For a region of <span className="text-accent font-bold">2+ billion people</span>,<br />
                we should have <span className="text-primary font-bold">hundreds</span> of such companies.
              </p>
            </div>
          </div>

          {/* The Root Causes */}
          <div className="bg-gradient-to-br from-danger/10 via-warning/5 to-danger/10 border-2 border-danger/40 rounded-3xl p-10 md:p-12">
            <h3 className="text-3xl font-bold text-danger mb-6 text-center font-heading">
              Why So Few? The Real Barriers
            </h3>
            <div className="space-y-6 text-lg text-gray-300 leading-relaxed max-w-4xl mx-auto">
              <div className="flex items-start gap-4">
                <span className="text-2xl">❌</span>
                <p>
                  <span className="text-white font-semibold">Fear of failure drilled from childhood</span> — "Beta, secure job lelo, business risky hai"
                </p>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-2xl">❌</span>
                <p>
                  <span className="text-white font-semibold">Society's toxic judgment</span> — "Log kya kahenge if you fail?"
                </p>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-2xl">❌</span>
                <p>
                  <span className="text-white font-semibold">No room for exploration</span> — Career decided at 18, no experimentation allowed
                </p>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-2xl">❌</span>
                <p>
                  <span className="text-white font-semibold">System-wide discouragement</span> — Teachers, bosses, elders all pushing the same "safe" path
                </p>
              </div>
              <div className="flex items-start gap-4">
                <span className="text-2xl">❌</span>
                <p>
                  <span className="text-white font-semibold">Those in power blocking innovation</span> — People with authority treating new ideas as threats
                </p>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t-2 border-danger/30 text-center">
              <p className="text-2xl md:text-3xl font-bold text-white">
                We train our youth to be <span className="text-warning">obedient workers</span>,<br />
                not <span className="text-primary">independent thinkers</span> or <span className="text-accent">bold creators</span>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechnologyFreedom;